import { appStore } from "@src/stores/AppStore";
import { action, makeObservable, observable } from "mobx";
import { DisclosureState, type DisclosureStateArgs } from "./DisclosureState";

export interface MinimizableDisclosureStateArgs<
  AdditionalDataType extends object,
> extends DisclosureStateArgs<AdditionalDataType> {
  modalId: string;
}

export class MinimizableDisclosureState<
  AdditionalDataType extends object,
> extends DisclosureState<AdditionalDataType> {
  readonly modalId: string;

  @observable.ref
  isMinimized = false;

  constructor(args: MinimizableDisclosureStateArgs<AdditionalDataType>) {
    super(args);
    this.modalId = args.modalId;
    makeObservable(this);
  }

  @action.bound
  onMinimize(title: string): void {
    this.isMinimized = true;
    this.close();
    appStore.UIStore.dialogs.minimizeModal(this.modalId, {
      title,
      onMaximize: () => {
        appStore.UIStore.dialogs.maximizeModal(this.modalId);
        this.onMaximize();
      },
    });
  }

  @action.bound
  onMaximize(): void {
    this.isMinimized = false;
    this.open();
  }

  onClose() {
    super.onClose();
    appStore.UIStore.dialogs.closeModal(this.modalId);
  }

  onOpen(additionalData?: AdditionalDataType): void {
    if (appStore.UIStore.dialogs.modalStack.has(this.modalId)) {
      appStore.UIStore.dialogs.warningModalDisclosure.onOpen({
        modalId: this.modalId,
        onConfirm: () => {
          this.onClose();
          super.onOpen(additionalData);
        },
      });
      return;
    }

    super.onOpen(additionalData);
  }
}
